import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "levelrule" }
const _hoisted_2 = { class: "levelrule-header" }
const _hoisted_3 = { class: "level-badge" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = {
  key: 0,
  class: "color-display"
}
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = {
  key: 0,
  class: "disabled-text"
}
const _hoisted_9 = {
  key: 0,
  class: "empty-tip"
}

import { ref, computed, onMounted } from 'vue';
import { useApi } from '@/hooks/useApi';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Delete, Top, Bottom } from '@element-plus/icons-vue';
import { useBaseAsync } from 'hpsfir-managebase-ui';

// 本地使用的等级规则类型（数值类型）
interface LocalLevelRule {
  name: string;
  color: string;
  growthValue: number;
  discount?: number;
}

// 颜色选项

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const colorOptions = [
  { value: '#36643f', label: '墨绿' },
  { value: '#3c6585', label: '蓝灰' },
  { value: '#b6860f', label: '金棕' },
  { value: '#2158ba', label: '藏蓝' },
  { value: '#705481', label: '紫棕' },
  { value: '#7173d2', label: '灰紫' },
  { value: '#b23323', label: '砖红' },
];

// 默认等级（不存数据库）
const defaultLevel: LocalLevelRule = {
  name: '普通',
  color: '#999999',
  growthValue: 0,
  discount: undefined,
};

const levelList = ref<LocalLevelRule[]>([]);
const saving = ref(false);
const loading = ref(false);

// 显示列表（包含默认等级）
const displayList = computed(() => [defaultLevel, ...levelList.value]);

// 获取颜色标签
const getColorLabel = (color: string) => {
  if (color === '#999999') {
    return '灰白';
  }
  const option = colorOptions.find((item) => item.value === color);
  return option ? option.label : color;
};

// 创建空等级
const createEmptyLevel = (growthValue = 1): LocalLevelRule => ({
  name: '',
  color: '#36643f',
  growthValue,
  discount: undefined,
});

// 加载配置
const loadConfig = useBaseAsync(async() => {
  loading.value = true;
  try {
    const res = await useApi().userLevelRuleApi.listUserLevelRuleuserLevelRule();
    const rules = res.data.data || [];
    if (rules.length > 0) {
      // 后端返回字符串，转换为数字
      levelList.value = rules.map((rule) => ({
        ...rule,
        growthValue: rule.growthValue === undefined ? 0 : Number(rule.growthValue),
        discount: rule.discount === undefined || rule.discount === '' ? undefined : Number(rule.discount),
      }));
    } else {
      levelList.value = [createEmptyLevel()];
    }
  } finally {
    loading.value = false;
  }
});

// 保存配置
const handleSave = useBaseAsync(async() => {
  // 验证
  for (let i = 0; i < levelList.value.length; i++) {
    const level = levelList.value[i];
    if (!level.name) {
      ElMessage.error(`第 ${i + 1} 级的等级名称不能为空`);
      return;
    }
    if (level.growthValue === undefined || level.growthValue === null) {
      ElMessage.error(`第 ${i + 1} 级的所需成长值不能为空`);
      return;
    }
    if (i > 0 && level.growthValue <= levelList.value[i - 1].growthValue) {
      ElMessage.error(`第 ${i + 1} 级的成长值必须大于第 ${i} 级`);
      return;
    }
    if (level.discount !== undefined && level.discount !== null) {
      if (level.discount <= 0 || level.discount >= 10) {
        ElMessage.error(`第 ${i + 1} 级的折扣必须大于0且小于10`);
        return;
      }
    }
  }

  saving.value = true;
  try {
    // 数字转换为字符串发送给后端
    const rulesData = levelList.value.map((rule) => ({
      ...rule,
      growthValue: String(rule.growthValue),
      discount: rule.discount === undefined ? undefined : String(rule.discount),
    }));
    await useApi().userLevelRuleApi.setUserLevelRulesuserLevelRule({
      rules: rulesData,
    });
    ElMessage.success('保存成功');
  } finally {
    saving.value = false;
  }
});

// 在前面插入
const handleInsertBefore = (index: number) => {
  const prevGrowthValue = index > 0 ? levelList.value[index - 1].growthValue : 0;
  const currentGrowthValue = levelList.value[index].growthValue;
  const newGrowthValue = Math.floor((prevGrowthValue + currentGrowthValue) / 2);
  levelList.value.splice(index, 0, createEmptyLevel(newGrowthValue));
};

// 在后面插入
const handleInsertAfter = (index: number) => {
  const currentGrowthValue = levelList.value[index].growthValue;
  const nextGrowthValue = index < levelList.value.length - 1
    ? levelList.value[index + 1].growthValue
    : currentGrowthValue + 100;
  const newGrowthValue = Math.floor((currentGrowthValue + nextGrowthValue) / 2);
  levelList.value.splice(index + 1, 0, createEmptyLevel(newGrowthValue));
};

// 删除
const handleDelete = async(index: number) => {
  await ElMessageBox.confirm('确定要删除该等级吗？', '提示', {
    type: 'warning',
    confirmButtonText: '确认',
    cancelButtonText: '取消',
  });
  levelList.value.splice(index, 1);
};

// 添加第一个等级
const handleAdd = () => {
  levelList.value.push(createEmptyLevel());
};

onMounted(() => {
  loadConfig();
});

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_input_number = _resolveComponent("el-input-number")!
  const _component_el_button_group = _resolveComponent("el-button-group")!
  const _component_el_table = _resolveComponent("el-table")!
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_el_button, {
        type: "primary",
        onClick: _unref(handleSave),
        loading: saving.value
      }, {
        default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
          _createTextVNode(" 保存配置 ", -1)
        ]))]),
        _: 1
      }, 8, ["onClick", "loading"])
    ]),
    _createVNode(_component_el_table, {
      data: displayList.value,
      border: "",
      class: "levelrule-table"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_table_column, {
          label: "等级",
          width: "100",
          align: "center"
        }, {
          default: _withCtx(({ $index }) => [
            _createElementVNode("span", _hoisted_3, "Lv." + _toDisplayString($index + 1), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_el_table_column, {
          label: "等级名称",
          "min-width": "150"
        }, {
          default: _withCtx(({ row, $index }) => [
            ($index === 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(row.name), 1))
              : (_openBlock(), _createBlock(_component_el_input, {
                  key: 1,
                  modelValue: row.name,
                  "onUpdate:modelValue": ($event: any) => ((row.name) = $event),
                  placeholder: "请输入等级名称"
                }, null, 8, ["modelValue", "onUpdate:modelValue"]))
          ]),
          _: 1
        }),
        _createVNode(_component_el_table_column, {
          label: "等级颜色",
          width: "180"
        }, {
          default: _withCtx(({ row, $index }) => [
            ($index === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createElementVNode("span", null, _toDisplayString(getColorLabel(row.color)), 1)
                ]))
              : (_openBlock(), _createBlock(_component_el_select, {
                  key: 1,
                  modelValue: row.color,
                  "onUpdate:modelValue": ($event: any) => ((row.color) = $event),
                  placeholder: "请选择颜色"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(colorOptions, (item) => {
                      return _createVNode(_component_el_option, {
                        key: item.value,
                        label: item.label,
                        value: item.value
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", {
                            class: "color-block",
                            style: _normalizeStyle({ backgroundColor: item.value })
                          }, null, 4),
                          _createElementVNode("span", null, _toDisplayString(item.label), 1)
                        ]),
                        _: 2
                      }, 1032, ["label", "value"])
                    }), 64))
                  ]),
                  _: 1
                }, 8, ["modelValue", "onUpdate:modelValue"]))
          ]),
          _: 1
        }),
        _createVNode(_component_el_table_column, {
          label: "所需成长值",
          "min-width": "150"
        }, {
          default: _withCtx(({ row, $index }) => [
            ($index === 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(row.growthValue), 1))
              : (_openBlock(), _createBlock(_component_el_input_number, {
                  key: 1,
                  modelValue: row.growthValue,
                  "onUpdate:modelValue": ($event: any) => ((row.growthValue) = $event),
                  min: $index === 1 ? 1 : (levelList.value[$index - 2]?.growthValue || 0) + 1,
                  controls: false,
                  placeholder: "请输入所需成长值",
                  style: {"width":"100%"}
                }, null, 8, ["modelValue", "onUpdate:modelValue", "min"]))
          ]),
          _: 1
        }),
        _createVNode(_component_el_table_column, {
          label: "折扣",
          width: "150"
        }, {
          default: _withCtx(({ row, $index }) => [
            ($index === 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_7, "-"))
              : (_openBlock(), _createBlock(_component_el_input_number, {
                  key: 1,
                  modelValue: row.discount,
                  "onUpdate:modelValue": ($event: any) => ((row.discount) = $event),
                  min: 0.01,
                  max: 9.99,
                  precision: 2,
                  step: 0.1,
                  controls: false,
                  placeholder: "可选",
                  style: {"width":"100%"}
                }, null, 8, ["modelValue", "onUpdate:modelValue"]))
          ]),
          _: 1
        }),
        _createVNode(_component_el_table_column, {
          label: "操作",
          width: "200",
          align: "center"
        }, {
          default: _withCtx(({ $index }) => [
            ($index === 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_8, "默认等级"))
              : (_openBlock(), _createBlock(_component_el_button_group, { key: 1 }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_button, {
                      size: "small",
                      onClick: ($event: any) => (handleInsertBefore($index - 1)),
                      icon: _unref(Top)
                    }, {
                      default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                        _createTextVNode(" 前增 ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["onClick", "icon"]),
                    _createVNode(_component_el_button, {
                      size: "small",
                      onClick: ($event: any) => (handleInsertAfter($index - 1)),
                      icon: _unref(Bottom)
                    }, {
                      default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                        _createTextVNode(" 后增 ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["onClick", "icon"]),
                    _createVNode(_component_el_button, {
                      size: "small",
                      type: "danger",
                      onClick: ($event: any) => (handleDelete($index - 1)),
                      icon: _unref(Delete),
                      disabled: levelList.value.length <= 1
                    }, null, 8, ["onClick", "icon", "disabled"])
                  ]),
                  _: 2
                }, 1024))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["data"]),
    (levelList.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: handleAdd
          }, {
            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
              _createTextVNode("添加第一个等级", -1)
            ]))]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true)
  ])), [
    [_directive_loading, loading.value]
  ])
}
}

})